import { FormController } from "$root/third-party-integrations/react-hook-form";
import { multiRef, valueByPath } from "$root/utils";
import { ClickableDiv, Icon, type StylableProps } from "@mdotm/shadcn/legacy/components";
import { type NodeOrFn, renderNodeOrFn } from "@mdotm/shadcn/legacy/react-extensions";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import type { Control, FieldValues, FormState, Path } from "react-hook-form";
import { TagBadge } from "$root/ui-lib/table/custom-column/tags/Tag";
import {
	type DataAttributesProps,
	FormField,
	Label,
	Select,
	type SelectTriggerProps,
} from "@mdotm/shadcn/mdotm/components";
import { instanceEqualsOrFastDeepEqual } from "@mdotm/shadcn/legacy/utils";
import { ForEach } from "@mdotm/utils/react-extra";

export type TagSelectOption<T> = {
	label: string;
	value: T;
	color?: string;
};

type TagSelectFieldProps<TForm extends FieldValues, TValue> = StylableProps & {
	control: Control<TForm, any>;
	formState: FormState<TForm>;
	name: Path<TForm>;
	label: NodeOrFn<{ htmlFor: string }>;
	options: Array<TagSelectOption<TValue>>;
	multi: boolean;
	enableSearch?: boolean;
	enableClear?: boolean;
} & DataAttributesProps;

/**
 * Extra props threaded to the trigger via the Select's `extraTriggerProps`. The new Select renders the
 * trigger through `PopoverTrigger asChild`, so this component is a `forwardRef` that spreads the
 * Radix-injected props (`onClick`, `aria-*`, `data-state`) onto its underlying `<button>`. `fieldRef`
 * carries the react-hook-form ref so focus-on-error still works alongside Radix's own ref.
 */
type TagSelectTriggerExtraProps = {
	selectedValues: unknown[];
	tagOptions: Array<TagSelectOption<unknown>>;
	fieldRef: React.Ref<HTMLButtonElement>;
	placeholder?: string;
	onRemoveOption(value: unknown): void;
};

const TagSelectTrigger = forwardRef<HTMLButtonElement, SelectTriggerProps & TagSelectTriggerExtraProps>(
	function TagSelectTrigger(
		{
			open,
			triggerLabel: _triggerLabel,
			isEmpty: _isEmpty,
			isMulti: _isMulti,
			disabled,
			size,
			invalid,
			onKeyDown,
			enableClear,
			onClear,
			selectedValues,
			tagOptions: options,
			fieldRef,
			placeholder = "Select...",
			onRemoveOption,
			...rest
		},
		ref,
	) {
		const containerRef = useRef<HTMLDivElement>(null);
		const [visibleCount, setVisibleCount] = useState(selectedValues.length);

		useEffect(() => {
			const container = containerRef.current;
			if (!container) {
				return;
			}

			const observer = new ResizeObserver(() => {
				const children = Array.from(container.children) as HTMLElement[];
				if (children.length === 0) {
					setVisibleCount(0);
					return;
				}

				const containerRight = container.getBoundingClientRect().right;
				// reserve space for "+N" badge (~40px)
				const reservedSpace = selectedValues.length > 0 ? 40 : 0;
				let count = 0;
				for (const child of children) {
					const childRight = child.getBoundingClientRect().right;
					if (childRight <= containerRight - reservedSpace) {
						count++;
					} else {
						break;
					}
				}
				setVisibleCount(Math.max(count, selectedValues.length > 0 ? 1 : 0));
			});

			observer.observe(container);
			return () => observer.disconnect();
		}, [selectedValues.length]);

		const selectedOptions = selectedValues
			.map((value) => options.find((o) => instanceEqualsOrFastDeepEqual(o.value, value)))
			.filter((o): o is TagSelectOption<unknown> => o != null);

		const overflowCount = selectedOptions.length - visibleCount;

		return (
			<button
				ref={multiRef(ref, fieldRef)}
				disabled={disabled}
				data-invalid={invalid}
				type="button"
				onKeyDown={onKeyDown}
				className={[
					"relative flex flex-row items-center justify-between w-full rounded-[4px] font-title",
					"border text-[#1d2433] placeholder:text-[var(--palette-N300)] border-[var(--palette-N200)] focus:border-[var(--palette-N500)] focus:outline-hidden",
					"bg-[#ffffff] disabled:border-[var(--palette-N100)] disabled:text-[var(--palette-N400)] disabled:bg-[var(--palette-N100)]",
					"data-[invalid=true]:border-[var(--palette-D500)]",
					size === "large" ? "h-[38px] px-[16px]" : size === "x-small" ? "h-[26px] px-1.5" : "h-[34px] px-2.5",
				].join(" ")}
				{...rest}
			>
				{selectedOptions.length === 0 ? (
					<span className="text-[#80848B] text-[14px] whitespace-nowrap overflow-hidden text-ellipsis mr-3">
						{placeholder}
					</span>
				) : (
					<div className="flex items-center gap-1 min-w-0 overflow-hidden mr-3 grow" ref={containerRef}>
						<ForEach collection={selectedOptions}>
							{(opt) => (
								<TagBadge
									key={opt.label}
									label={opt.label}
									color={opt.color}
									onRemove={(e) => {
										e.stopPropagation();
										onRemoveOption?.(opt.value);
									}}
								/>
							)}
						</ForEach>
					</div>
				)}
				{overflowCount > 0 && <span className="shrink-0 text-[12px] text-[#80848B] mr-1">+{overflowCount}</span>}
				{enableClear && onClear && selectedOptions.length > 0 && (
					<ClickableDiv
						className="shrink-0 inline-flex mr-1 cursor-pointer border-none bg-transparent p-0"
						onClick={(e) => {
							e.stopPropagation();
							onClear();
						}}
					>
						<Icon icon="Close" size={16} color="var(--palette-N500)" />
					</ClickableDiv>
				)}
				<span
					className="transition-transform data-[open=true]:transform-[rotateX(180deg)] flex shrink-0"
					data-open={open}
				>
					<Icon icon="Down" />
				</span>
			</button>
		);
	},
);

export function TagSelectField<TForm extends FieldValues, TValue>({
	label,
	formState,
	name,
	control,
	className,
	style,
	options,
	multi,
	enableSearch = true,
	enableClear = true,
	...forward
}: TagSelectFieldProps<TForm, TValue>): React.JSX.Element {
	const selectOptions = useMemo(
		() =>
			options.map((opt) => ({
				label: opt.label,
				value: opt.value,
				children: () => (
					<span className="text-start flex items-center grow relative z-0 text-[14px]">
						<TagBadge label={opt.label} color={opt.color} />
						<Icon
							icon="Outline"
							className="ml-auto absolute right-2 top-1/2 -translate-y-1/2 invisible group-aria-selected/option:visible"
							size={20}
							style={{ display: "inline-block" }}
						/>
					</span>
				),
			})),
		[options],
	);

	return (
		<FormField
			className={className}
			style={style}
			label={({ htmlFor }) =>
				typeof label === "string" ? (
					<Label htmlFor={htmlFor} className="mb-px">
						{label}
					</Label>
				) : (
					renderNodeOrFn(label, { htmlFor })
				)
			}
			error={(valueByPath(formState.errors, name) as { message?: string })?.message}
		>
			{(fieldProps) => (
				<FormController
					control={control}
					name={name}
					render={({ field: { ref, ...controllerProps } }) => {
						const currentValue = controllerProps.value;
						const selectedValues: TValue[] = multi
							? Array.isArray(currentValue)
								? currentValue
								: []
							: currentValue != null
								? [currentValue as TValue]
								: [];

						return (
							<Select
								{...fieldProps}
								{...controllerProps}
								onChange={controllerProps.onChange}
								options={selectOptions}
								multi={multi}
								enableSearch={enableSearch}
								enableClear={enableClear}
								Trigger={TagSelectTrigger}
								extraTriggerProps={
									{
										selectedValues,
										tagOptions: options,
										fieldRef: ref,
										onRemoveOption: (value) =>
											controllerProps.onChange(
												selectedValues.filter((currentVal) => !instanceEqualsOrFastDeepEqual(currentVal, value)),
											),
									} satisfies TagSelectTriggerExtraProps
								}
								{...(forward as any)}
							/>
						);
					}}
				/>
			)}
		</FormField>
	);
}
